<template>
    <Panel title="License" :loading="loading" @closePanel="closePanel">
        <!-- Data -->
        <template v-if="license">
            <div class="mb-2">
                <LicenseStatus :license="license" />
            </div>

            <Sheet name="Details">
                <Row name="Reference">{{ license.reference }}</Row>
                <Row v-if="formattedType" name="Type">{{ formattedType }}</Row>
                <Row v-if="paidBy" name="Paid by"
                    ><a href="#" @click.prevent="openResource(license.paid_by_id, license.paid_by_type)"
                        ><Icon :icon="getIconFromResource(license.paid_by_type)" />&nbsp;{{ paidBy }}</a
                    ></Row
                >
                <Row v-if="paidFor" name="Paid for"
                    ><a href="#" @click.prevent="openResource(license.paid_for_id, license.paid_for_type)"
                        ><Icon :icon="getIconFromResource(license.paid_for_type)" />&nbsp;{{ paidFor }}</a
                    ></Row
                >
                <Row v-if="license.used_by_id" name="Used for"
                    ><a href="#" @click.prevent="openResource(license.used_by_id, license.used_by_type)"
                        ><Icon :icon="getIconFromResource(license.used_by_type)" />&nbsp;{{ license.used_by_id }}</a
                    ></Row
                >
                <Row name="Used at" v-if="license.used_at">{{ license.used_at | formatDate }}</Row>
                <Row v-if="license.metadata && Object.keys(license.metadata).length > 0" name="Metadata">
                    <RawOutput :item="license.metadata" />
                </Row>
            </Sheet>

            <Sheet name="Dates">
                <Row name="Created at">{{ license.created_at | formatDate }}</Row>
                <Row name="Updated at">{{ license.updated_at | formatDate }}</Row>
                <Row name="Deleted at" v-if="license.deleted_at">{{ license.deleted_at | formatDate }}</Row>
            </Sheet>
        </template>

        <!-- Footer -->
        <div v-if="license" class="mt-4">
            <ID name="Hexeko" :value="license.id" />
        </div>

        <template
            v-slot:actions
            v-if="license && !license.used_at && $store.getters.userHasPermission('manageLicenses')"
        >
            <!-- Actions -->
            <Dropdown small title="Actions" position="right">
                <DropdownItem
                    confirm
                    @confirm="deleteLicense"
                    text="Are you sure you want to delete this license?"
                    icon="trash"
                >
                    Delete license
                </DropdownItem>
            </Dropdown>
        </template>
    </Panel>
</template>

<script>
    import { Alerts, Dropdown, DropdownItem, ID, Panel, Row, Sheet } from '@tech_hexeko/design-system'
    import Modals from '@tech_hexeko/design-system/src/mixins/Modals'
    import { formatError } from '@/plugins/errors.js'
    import RawOutput from '@/components/RawOutput'
    import LicenseStatus from './status'
    import TYPES from './types'

    const RESOURCES = {
        card: {
            icon: 'credit-card',
            model: 'App\\Models\\Card',
            panel: 'cardPanel',
        },
        group: {
            icon: 'users',
            model: 'App\\Models\\Group',
            panel: 'groupPanel',
        },
        user: {
            icon: 'user',
            model: 'App\\Models\\User',
            panel: 'userPanel',
        },
        wallet: {
            icon: 'wallet',
            model: 'App\\Models\\Wallet',
            panel: 'walletPanel',
        },
    }

    export default {
        name: 'LicensePanel',
        mixins: [Modals],
        components: {
            Dropdown,
            DropdownItem,
            ID,
            LicenseStatus,
            Panel,
            RawOutput,
            Row,
            Sheet,
        },
        data() {
            return {
                loading: true,
                license: null,
                isUpdated: false,
            }
        },
        props: {
            id: {
                type: String,
                required: true,
            },
        },
        computed: {
            formattedType() {
                return TYPES[this.license?.license_type?.name]?.name
            },
            paidBy() {
                return this.license?.paid_by?.name || this.license?.paid_by_id
            },
            paidFor() {
                return this.license?.paid_for?.name || this.license?.paid_for_id
            },
        },
        methods: {
            closePanel(args) {
                if (this.isUpdated) args = { refresh: true }
                this.$emit('closePanel', args)
            },
            async deleteLicense() {
                try {
                    this.loading = true
                    await this.$api.licenses.delete(this.license.id)
                    Alerts.notificationSuccess('License deleted')
                    this.isUpdated = true
                    this.closePanel()
                } catch (error) {
                    const errorTxt = formatError(error)
                    Alerts.notificationError(errorTxt)
                } finally {
                    this.loading = false
                }
            },
            async fetchData() {
                try {
                    this.loading = true
                    this.license = await this.$api.licenses.show(this.id, { includes: ['paid_for', 'paid_by'] })
                } catch (error) {
                    const errorTxt = formatError(error)
                    Alerts.notificationError(errorTxt)
                    this.closePanel()
                } finally {
                    this.loading = false
                }
            },
            getIconFromResource(model) {
                const modelIcon = Object.values(RESOURCES).find((resource) => resource.model === model)?.icon
                return modelIcon || 'search'
            },
            openResource(id, model) {
                const panelName = Object.values(RESOURCES).find((resource) => resource.model === model)?.panel
                if (panelName) {
                    this.openPanel(panelName, id)
                } else {
                    Alerts.notificationError('An error prevents this resource from opening')
                }
            },
        },
        mounted() {
            this.fetchData()
        },
    }
</script>
